/**
 * MapBookRide — Map-First Ride Booking (Phase 1-3 + P0/P1 Bug Fixes)
 * /ride/book
 * 
 * Coordinates are the source of truth. Address text helps the driver.
 * 
 * Flow: Map → "Where are you going?" → Pin → Quote → Pickup → Route → Driver notes → Payment → Book
 *
 * P0 FIXES APPLIED:
 *   1. All inputs force text-gray-900 + explicit color style (mobile text visibility)
 *   2. Payment method selection added to Step 6 (Fygaro JMD / Stripe USD — no cash)
 *   3. Ride creation POST includes payment_method + currency for backend alignment
 *   4. Post-booking stays in-component instead of redirecting to /ride/track (which caused crash)
 *   5. Honest payment messaging: card payment not claimed as completed when it's not yet charged
 *
 * P1 FIXES APPLIED:
 *   6. Fare locked in sessionStorage before login redirect; restored with clear messaging
 *   7. Search UX: loading spinner, no-results state, error state, visible dropdown
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PostTripConfirmation from './PostTripConfirmation';
import StripePaymentForm from './StripePaymentForm';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Search, MapPin, Navigation, Crosshair, ArrowLeft, ArrowRight,
  Route, Clock, DollarSign, Car, Share2, CheckCircle, Loader2,
  GripVertical, X, Locate, Send, Shield, MessageSquare, Copy,
  ChevronRight, Sparkles, ExternalLink, Phone, Mail,
  AlertCircle, CreditCard, Wallet, Home, Briefcase,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const pickupIcon = L.divIcon({
  className: '',
  html: `<div style="background:#16a34a;width:34px;height:34px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><circle cx="12" cy="12" r="4"/></svg></div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});
const dropoffIcon = L.divIcon({
  className: '',
  html: `<div style="background:#dc2626;width:34px;height:34px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/></svg></div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});

const STEPS = { DESTINATION: 1, CONFIRM_DEST: 2, PICKUP: 3, ROUTE_REVIEW: 4, DRIVER_NOTES: 5, SUMMARY: 6, PAYMENT: 7 };

const QUICK_PICKS = [
  { name: 'Norman Manley Airport', lat: 17.9356, lng: -76.7875, parish: 'Kingston', type: 'airport' },
  { name: 'Half Way Tree', lat: 18.0157, lng: -76.7898, parish: 'St. Andrew', type: 'landmark' },
  { name: 'Sovereign Centre', lat: 18.0092, lng: -76.7567, parish: 'St. Andrew', type: 'mall' },
  { name: 'UWI Mona', lat: 18.0050, lng: -76.7467, parish: 'St. Andrew', type: 'university' },
  { name: 'Portmore Mall', lat: 17.9542, lng: -76.8800, parish: 'St. Catherine', type: 'mall' },
  { name: 'Spanish Town Hospital', lat: 18.0000, lng: -76.9567, parish: 'St. Catherine', type: 'hospital' },
];

// P0 fix: Explicit input styling constant — dark text on all devices
const INPUT_STYLE = "bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2";
const INPUT_COLOR = { color: '#111827', WebkitTextFillColor: '#111827' };

const JMD_TO_USD = 155;

// ─── Shared Jamaica landmarks — SAME dataset as AddressInput/SendPackage ───
// Landmarks assist discovery; they supplement Nominatim, never block it.
const JAMAICA_LANDMARKS = {
  "JUTC Halfway Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Half Way Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Halfway Tree": { lat: 18.0157, lng: -76.7898, parish: "St. Andrew" },
  "Constant Spring": { lat: 18.0258, lng: -76.7807, parish: "St. Andrew" },
  "Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "King Street Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "King Street Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "31 King Street": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "31 King Street Downtown Kingston": { lat: 17.9714, lng: -76.7920, parish: "Kingston" },
  "New Kingston": { lat: 18.0075, lng: -76.7833, parish: "St. Andrew" },
  "Liguanea": { lat: 18.0192, lng: -76.7633, parish: "St. Andrew" },
  "Cross Roads": { lat: 18.0042, lng: -76.7875, parish: "St. Andrew" },
  "Portmore": { lat: 17.9500, lng: -76.8833, parish: "St. Catherine" },
  "Greater Portmore": { lat: 17.9500, lng: -76.8833, parish: "St. Catherine" },
  "Spanish Town": { lat: 17.9917, lng: -76.9550, parish: "St. Catherine" },
  "Horizon Park": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "Horizon Park Spanish Town": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "3 Regal Ave": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "3 Regal Ave Horizon Park": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "Regal Ave Horizon Park Spanish Town": { lat: 17.9850, lng: -76.9400, parish: "St. Catherine" },
  "Whitewater Meadows": { lat: 17.9880, lng: -76.9450, parish: "St. Catherine" },
  "Old Harbour": { lat: 17.9417, lng: -77.1083, parish: "St. Catherine" },
  "Linstead": { lat: 18.1333, lng: -77.0333, parish: "St. Catherine" },
  "Montego Bay": { lat: 18.4762, lng: -77.8939, parish: "St. James" },
  "Ocho Rios": { lat: 18.4074, lng: -77.1051, parish: "St. Ann" },
  "Mandeville": { lat: 18.0419, lng: -77.5033, parish: "Manchester" },
  "May Pen": { lat: 17.9619, lng: -77.2478, parish: "Clarendon" },
  "Negril": { lat: 18.2678, lng: -78.3492, parish: "Westmoreland" },
  "Port Antonio": { lat: 18.1747, lng: -76.4500, parish: "Portland" },
  "Norman Manley Airport": { lat: 17.9356, lng: -76.7875, parish: "Kingston" },
  "Sangster Airport": { lat: 18.5033, lng: -77.9133, parish: "St. James" },
  "Spanish Town Hospital": { lat: 17.9920, lng: -76.9545, parish: "St. Catherine" },
  "University Hospital": { lat: 18.0156, lng: -76.7489, parish: "St. Andrew" },
  "Kingston Public Hospital": { lat: 17.9756, lng: -76.7858, parish: "Kingston" },
  "Sovereign Centre": { lat: 18.0092, lng: -76.7567, parish: "St. Andrew" },
  "UWI Mona": { lat: 18.0050, lng: -76.7467, parish: "St. Andrew" },
  "Portmore Mall": { lat: 17.9542, lng: -76.8800, parish: "St. Catherine" },
  "Papine": { lat: 18.0208, lng: -76.7392, parish: "St. Andrew" },
  "Morant Bay": { lat: 17.8814, lng: -76.4088, parish: "St. Thomas" },
  "Bull Bay": { lat: 17.9333, lng: -76.6833, parish: "St. Andrew" },
};

const PARISHES = [
  "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
  "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
  "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine"
];

// Pure Haversine — same formula as SendPackage
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon/2) * Math.sin(dLon/2);
  return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
}

// ─── Nominatim search — SAME approach as AddressInput/SendPackage ───
// No word stripping — pass full user query to Nominatim with Jamaica bias
async function nominatimSearch(query, limit = 8) {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ", Jamaica")}&limit=${limit}&addressdetails=1`,
    { headers: { 'User-Agent': 'LiantiaoGO/5.0' } }
  );
  return response.json();
}

// ─── Landmark matching — word-level matching aligned with AddressInput ───
// Landmarks supplement Nominatim results, never replace them
function matchLandmarks(query) {
  const queryLower = query.toLowerCase();
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);
  return Object.entries(JAMAICA_LANDMARKS)
    .filter(([name]) => {
      const nameLower = name.toLowerCase();
      // Full substring match in either direction
      if (nameLower.includes(queryLower) || queryLower.includes(nameLower)) return true;
      // Word-level: at least 2 query words must appear in landmark name, OR
      // one word of 5+ chars matches (avoids false positives from short words like "town")
      const wordMatches = queryWords.filter(word => nameLower.includes(word));
      return wordMatches.length >= 2 || wordMatches.some(w => w.length >= 5);
    })
    .map(([name, coords]) => ({
      id: `lm_${name}`, name, display: `${name}, ${coords.parish}, Jamaica`,
      street: name, town: coords.parish, parish: coords.parish,
      lat: coords.lat, lng: coords.lng, source: 'landmark',
    }));
}

// Parse Nominatim results into unified format
function parseNominatimResults(data) {
  return data.map((item) => {
    const addr = item.address || {};
    const house = addr.house_number || '';
    const road = addr.road || addr.pedestrian || '';
    const name = house && road ? `${house} ${road}` : (addr.amenity || addr.building || road || addr.suburb || addr.neighbourhood || item.display_name.split(',')[0]);
    let parish = '';
    for (const p of PARISHES) {
      if ((addr.state || '').toLowerCase().includes(p.toLowerCase()) ||
          (addr.county || '').toLowerCase().includes(p.toLowerCase()) ||
          (item.display_name || '').toLowerCase().includes(p.toLowerCase())) {
        parish = p; break;
      }
    }
    return {
      id: `nom_${item.place_id}`, name,
      display: item.display_name,
      street: road, town: addr.city || addr.town || addr.village || addr.suburb || '',
      parish, lat: parseFloat(item.lat), lng: parseFloat(item.lon), source: 'nominatim',
    };
  });
}

// Reverse geocode via Nominatim directly (no backend dependency)
async function reverseGeocodeNominatim(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      { headers: { 'User-Agent': 'LiantiaoGO/5.0' } }
    );
    const data = await res.json();
    if (data && data.address) {
      const addr = data.address;
      const house = addr.house_number || '';
      const road = addr.road || addr.pedestrian || '';
      const street = house && road ? `${house} ${road}` : road || addr.suburb || addr.neighbourhood || '';
      let parish = 'Kingston';
      for (const p of PARISHES) {
        if ((addr.state || '').toLowerCase().includes(p.toLowerCase()) ||
            (addr.county || '').toLowerCase().includes(p.toLowerCase()) ||
            (data.display_name || '').toLowerCase().includes(p.toLowerCase())) {
          parish = p; break;
        }
      }
      return {
        street, parish,
        city: addr.city || addr.town || addr.village || addr.suburb || '',
        full_address: data.display_name || '',
      };
    }
  } catch (e) { /* silent */ }
  return null;
}

async function fetchRoute(pickup, dropoff) {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${dropoff.lng},${dropoff.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.code === 'Ok' && data.routes?.[0]) {
      const r = data.routes[0];
      return { geometry: r.geometry, distance: r.distance, duration: r.duration };
    }
  } catch (e) { /* silent */ }
  return null;
}

async function fetchFareEstimate(pickup, dropoff, vehicleType = 'standard', routeDistanceKm = null, routeDurationMin = null) {
  try {
    const res = await fetch(`${API_URL}/api/rides/estimate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pickup: { address: pickup.name || '', coordinates: { lat: pickup.lat, lng: pickup.lng }, parish: pickup.parish || 'Kingston', city: pickup.town || '' },
        dropoff: { address: dropoff.name || '', coordinates: { lat: dropoff.lat, lng: dropoff.lng }, parish: dropoff.parish || 'Kingston', city: dropoff.town || '' },
        vehicle_type: vehicleType, is_tourist: false, priority_bonus: 0,
        route_distance_km: routeDistanceKm,
        route_duration_min: routeDurationMin,
      }),
    });
    return await res.json();
  } catch (e) { return null; }
}

// ─── Location Intelligence API ───
async function fetchLearnedSuggestions(query) {
  try {
    const res = await fetch(`${API_URL}/api/location-intelligence/suggest?q=${encodeURIComponent(query)}&limit=5`);
    const data = await res.json();
    return data.success ? data.results : [];
  } catch (e) { return []; }
}

async function fetchRecentLocations(token) {
  try {
    const res = await fetch(`${API_URL}/api/location-intelligence/recent?limit=5`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await res.json();
    return data.success ? data.results : [];
  } catch (e) { return []; }
}

function learnLocation(loc, originalQuery) {
  fetch(`${API_URL}/api/location-intelligence/learn`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      display_name: loc.name || loc.display || '',
      lat: loc.lat, lng: loc.lng,
      parish: loc.parish || '',
      source_type: loc.source || 'nominatim',
      original_query: originalQuery || '',
    }),
  }).catch(() => {});
}

function learnPinCorrection(name, origLat, origLng, corrLat, corrLng, parish) {
  fetch(`${API_URL}/api/location-intelligence/pin-correction`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      display_name: name, original_lat: origLat, original_lng: origLng,
      corrected_lat: corrLat, corrected_lng: corrLng, parish: parish || '',
    }),
  }).catch(() => {});
}

function learnTripCompletion(name, lat, lng, success) {
  fetch(`${API_URL}/api/location-intelligence/complete-trip`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ display_name: name, lat, lng, success }),
  }).catch(() => {});
}

async function fetchNearestLandmark(lat, lng) {
  try {
    const res = await fetch(`${API_URL}/api/location-intelligence/nearest?lat=${lat}&lng=${lng}`);
    const data = await res.json();
    return data.success ? data.nearest : null;
  } catch (e) { return null; }
}

export default function MapBookRide() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const userLocationRef = useRef(null); // Cache user GPS for search distance

  const [step, setStep] = useState(STEPS.DESTINATION);
  const [searchQuery, setSearchQuery] = useState('');
  const [typedQuery, setTypedQuery] = useState(''); // Preserves user's original typed text
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [destination, setDestination] = useState(null);
  const [pickup, setPickup] = useState(null);
  const [vehicleType, setVehicleType] = useState('standard');

  const [routeInfo, setRouteInfo] = useState(null);
  const [fareEstimate, setFareEstimate] = useState(null);
  const [fareLoading, setFareLoading] = useState(false);
  const [lockedFare, setLockedFare] = useState(null); // P1 fix: fare lock
  const [lockedRouteInfo, setLockedRouteInfo] = useState(null); // P1 fix: route lock

  const [driverNotes, setDriverNotes] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [gettingLocation2, setGettingLocation2] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookingState, setBookingState] = useState(null); // null | 'submitting' | 'confirmed'
  const [bookedRide, setBookedRide] = useState(null); // Stores ride data after successful booking
  const [showPostTrip, setShowPostTrip] = useState(false);
  const [savedPlaces, setSavedPlaces] = useState([]);

  // P0 fix: Payment method state
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [bookingCountdown, setBookingCountdown] = useState(null); // seconds remaining
  const [fygaroRef, setFygaroRef] = useState(null); // Fygaro ride reference
  const [fygaroRedirecting, setFygaroRedirecting] = useState(false);

  // Destination sharing
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareRecipientName, setShareRecipientName] = useState('');
  const [shareRecipientPhone, setShareRecipientPhone] = useState('');
  const [shareToken, setShareToken] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareStatus, setShareStatus] = useState(null); // null | 'pending' | 'submitted'
  const [shareSending, setShareSending] = useState(false);

  // Location Intelligence
  const [recentLocations, setRecentLocations] = useState([]);
  // Rider personalization
  const [riderPlaces, setRiderPlaces] = useState({ saved: [], frequent: [], recent: [] });
  const [riderLoaded, setRiderLoaded] = useState(false);
  const [nearestLandmark, setNearestLandmark] = useState(null);

  // Fetch rider-personalized destinations on mount
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) return;
    fetch(`${API_URL}/api/rides/rider/destinations`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        setRiderPlaces({
          saved: data.saved_places || [],
          frequent: data.frequently_booked || [],
          recent: data.recent_destinations || [],
        });
      }
      setRiderLoaded(true);
    })
    .catch(() => setRiderLoaded(true));
  }, []);

  // Fetch recent locations and saved places on mount if authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchRecentLocations(token).then(setRecentLocations).catch(() => {});
      fetch(`${API_URL}/api/location-intelligence/saved-places`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.success) setSavedPlaces(d.places || []); }).catch(() => {});
    }
  }, []);

  // Init map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { center: [18.1096, -77.2975], zoom: 9, zoomControl: false, attributionControl: false, maxZoom: 19 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, maxNativeZoom: 19 }).addTo(map);
    L.control.zoom({ position: 'topright' }).addTo(map);
    mapInstanceRef.current = map;
    // Silently get user GPS for search distance display
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { userLocationRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude }; },
        () => {}, { enableHighAccuracy: false, timeout: 5000 }
      );
    }
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  // Search — ranked: learned confirmed → landmarks → Nominatim
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSearchError(null);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (query.length < 2) { setSearchResults([]); setShowResults(false); return; }
    setShowResults(true);
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        // 1. Query learned locations (confirmed/trusted Jamaica intelligence)
        const learnedResults = await fetchLearnedSuggestions(query);
        const learnedMapped = learnedResults.map((r) => ({
          id: `learned_${r.normalized_name}`, name: r.display_name,
          display: `${r.display_name}, ${r.parish || 'Jamaica'}`,
          street: r.display_name, town: r.community || '', parish: r.parish || '',
          lat: r.lat, lng: r.lng, source: 'learned',
          confidence: r.confidence || 'suggested',
          score: r.score || 0,
          high_correction_flag: r.high_correction_flag || false,
        }));

        // 2. Match curated landmarks
        const landmarkResults = matchLandmarks(query);

        // 3. Nominatim search — full query, no stripping
        const data = await nominatimSearch(query, 8);
        const nomResults = parseNominatimResults(data);

        // 4. Merge all sources
        const merged = [...learnedMapped, ...landmarkResults, ...nomResults];

        // 5. Suppress overseas / non-Jamaica results
        const jamaicaFiltered = merged.filter((r) => {
          if (r.lat < 17.5 || r.lat > 18.6 || r.lng < -78.5 || r.lng > -76.1) return false;
          return true;
        });

        const seen = new Set();
        const deduped = jamaicaFiltered.filter((r) => {
          const key = `${r.lat.toFixed(3)}_${r.lng.toFixed(3)}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // 6. Add distance — from pickup if set, otherwise user GPS
        const ref = pickup ? { lat: pickup.lat, lng: pickup.lng } : userLocationRef.current;
        for (const r of deduped) {
          r.distance_km = ref ? parseFloat(haversineKm(ref.lat, ref.lng, r.lat, r.lng).toFixed(1)) : null;
          r.distance_from = pickup ? 'pickup' : 'gps';
        }

        // 7. Strict tiered sort: trusted > highly_confirmed > confirmed > landmark > nominatim
        //    Within each tier: closer results rank higher
        const confTier = { trusted: 0, highly_confirmed: 1, confirmed: 2, suggested: 3 };
        deduped.sort((a, b) => {
          // Learned results sort by confidence tier first
          if (a.source === 'learned' && b.source === 'learned') {
            const tierDiff = (confTier[a.confidence] ?? 3) - (confTier[b.confidence] ?? 3);
            if (tierDiff !== 0) return tierDiff;
          }
          // Then by source: learned > landmark > nominatim
          const srcOrder = { learned: 0, landmark: 1, nominatim: 2 };
          const srcDiff = (srcOrder[a.source] ?? 2) - (srcOrder[b.source] ?? 2);
          if (srcDiff !== 0) return srcDiff;
          // Within same tier+source, closer first
          if (a.distance_km != null && b.distance_km != null) return a.distance_km - b.distance_km;
          return 0;
        });

        setSearchResults(deduped.slice(0, 10));
        if (deduped.length === 0) setSearchError('No match found. Try a different address or nearby landmark.');
      } catch (e) {
        console.error('Search error:', e);
        setSearchResults([]);
        setSearchError('Search failed — check your connection and try again');
      }
      setSearching(false);
    }, 400);
  }, []);

  // Place marker helper
  const placeDropoffMarker = useCallback((loc) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (dropoffMarkerRef.current) map.removeLayer(dropoffMarkerRef.current);
    dropoffMarkerRef.current = L.marker([loc.lat, loc.lng], { icon: dropoffIcon, draggable: true })
      .addTo(map).bindTooltip(loc.name || 'Destination', { permanent: false, direction: 'top', offset: [0, -20] });
    dropoffMarkerRef.current.on('dragend', async (e) => {
      const { lat, lng } = e.target.getLatLng();
      const origLat = loc.lat, origLng = loc.lng;
      const geo = await reverseGeocodeNominatim(lat, lng);
      const newDest = {
        ...destination, lat, lng,
        name: geo?.street || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        display: geo?.full_address || '',
        address: geo?.full_address || '', parish: geo?.parish || destination?.parish || '', town: geo?.city || '',
      };
      setDestination(newDest);
      // Learn pin correction
      learnPinCorrection(loc.name || '', origLat, origLng, lat, lng, geo?.parish || '');
      toast.success('Pin moved — address and distance updated');
    });
    map.flyTo([loc.lat, loc.lng], 15, { duration: 1 });
  }, []);

  const placePickupMarker = useCallback((loc) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (pickupMarkerRef.current) map.removeLayer(pickupMarkerRef.current);
    pickupMarkerRef.current = L.marker([loc.lat, loc.lng], { icon: pickupIcon, draggable: true })
      .addTo(map).bindTooltip(loc.name || 'Pickup', { permanent: false, direction: 'top', offset: [0, -20] });
    pickupMarkerRef.current.on('dragend', async (e) => {
      const { lat, lng } = e.target.getLatLng();
      const geo = await reverseGeocodeNominatim(lat, lng);
      setPickup(prev => ({
        ...prev, lat, lng,
        name: geo?.street || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        display: geo?.full_address || '',
        address: geo?.full_address || '', parish: geo?.parish || prev?.parish || '', town: geo?.city || '',
      }));
      toast.success('Pickup pin moved — address and distance updated');
    });
  }, []);

  // Select destination — store full Nominatim address
  const selectDestination = useCallback((result) => {
    const loc = { ...result, lat: Number(result.lat), lng: Number(result.lng) };
    setDestination(loc);
    setTypedQuery(searchQuery);
    setSearchQuery(result.name);
    setShowResults(false);
    setStep(STEPS.CONFIRM_DEST);
    placeDropoffMarker(loc);
    // Selection confirmation with distance
    const ref = pickup ? { lat: pickup.lat, lng: pickup.lng } : userLocationRef.current;
    if (ref) {
      const d = haversineKm(ref.lat, ref.lng, loc.lat, loc.lng).toFixed(1);
      const label = pickup ? 'from pickup' : 'away';
      toast.success(`Selected: ${result.name} (~${d} km ${label})`);
    } else {
      toast.success(`Selected: ${result.name}`);
    }
    // Learn this selection
    learnLocation(loc, searchQuery);
    // Fetch nearest landmark for context
    fetchNearestLandmark(loc.lat, loc.lng).then(setNearestLandmark).catch(() => {});
  }, [placeDropoffMarker, searchQuery, pickup]);

  // Fetch fare when both points and route exist — uses OSRM road distance for accuracy
  // P1 fix: skip if fare is already locked (user confirmed route)
  useEffect(() => {
    if (!pickup || !destination) return;
    if (lockedFare) return; // Fare is frozen after route confirmation
    setFareLoading(true);
    const rdKm = routeInfo ? parseFloat(routeInfo.distance) : null;
    const rdMin = routeInfo ? routeInfo.duration : null;
    fetchFareEstimate(pickup, destination, vehicleType, rdKm, rdMin).then((data) => { setFareLoading(false); if (data) setFareEstimate(data); });
  }, [pickup?.lat, pickup?.lng, destination?.lat, destination?.lng, vehicleType, routeInfo?.distance, lockedFare]);

  // Draw route — freezes once calculated, only recalculates on location change
  const routeKeyRef = useRef(null);
  useEffect(() => {
    if (!pickup || !destination || !mapInstanceRef.current) return;
    const newKey = `${pickup.lat}_${pickup.lng}_${destination.lat}_${destination.lng}`;
    if (routeKeyRef.current === newKey) return; // Same route, skip
    routeKeyRef.current = newKey;
    const map = mapInstanceRef.current;
    if (routeLayerRef.current) { map.removeLayer(routeLayerRef.current); routeLayerRef.current = null; }
    fetchRoute(pickup, destination).then((route) => {
      if (route?.geometry) {
        const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        routeLayerRef.current = L.polyline(coords, { color: '#f59e0b', weight: 5, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }).addTo(map);
        map.fitBounds(routeLayerRef.current.getBounds(), { padding: [80, 80], maxZoom: 17 });
        setRouteInfo({ distance: (route.distance / 1000).toFixed(1), duration: Math.round(route.duration / 60) });
      }
    });
  }, [pickup?.lat, pickup?.lng, destination?.lat, destination?.lng]);

  // GPS
  const getCurrentLocation = useCallback(async (forField = 'pickup') => {
    if (!navigator.geolocation) { toast.error('GPS not available'); return; }
    if (forField === 'pickup') setGettingLocation(true);
    else setGettingLocation2(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        const geo = await reverseGeocodeNominatim(lat, lng);
        const loc = { lat, lng, name: geo?.street || geo?.suburb || 'Current Location', address: geo?.full_address || '', parish: geo?.parish || 'Kingston', town: geo?.city || geo?.town || '', source: 'gps' };
        if (forField === 'pickup') { setPickup(loc); placePickupMarker(loc); toast.success('Pickup location set'); setGettingLocation(false); }
        else { setDestination(loc); placeDropoffMarker(loc); setStep(STEPS.CONFIRM_DEST); toast.success('Destination set'); setGettingLocation2(false); }
      },
      () => {
        if (forField === 'pickup') setGettingLocation(false);
        else setGettingLocation2(false);
        toast.error('Could not get location. Please type your address.');
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }, [placePickupMarker, placeDropoffMarker]);

  // Recenter
  const recenterMap = () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (routeLayerRef.current) map.fitBounds(routeLayerRef.current.getBounds(), { padding: [80, 80] });
    else if (destination) map.flyTo([destination.lat, destination.lng], 15, { duration: 0.5 });
    else map.flyTo([18.1096, -77.2975], 9, { duration: 0.5 });
  };

  // ─── DESTINATION SHARING ───
  const sendDestinationRequest = async () => {
    if (!shareRecipientName.trim()) { toast.error('Please enter recipient name'); return; }
    const token = localStorage.getItem('token');
    if (!token) {
      sessionStorage.setItem('post_login_redirect', '/ride/book');
      toast.info('Please sign in to send destination requests');
      window.location.href = '/login';
      return;
    }
    setShareSending(true);
    try {
      const res = await fetch(`${API_URL}/api/ride-locations/request-location`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ recipient_name: shareRecipientName.trim(), recipient_phone: shareRecipientPhone.trim(), location_type: 'destination', ride_notes: 'Please confirm the destination location for my ride' }),
      });
      const data = await res.json();
      if (data.success) {
        setShareToken(data.token);
        setShareUrl(data.share_url);
        setShareStatus('pending');
        toast.success('Destination request created!');
      } else { toast.error('Failed to create request'); }
    } catch (e) { toast.error('Network error'); }
    setShareSending(false);
  };

  // Poll for share confirmation
  useEffect(() => {
    if (shareStatus !== 'pending' || !shareToken) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ride-locations/check/${shareToken}`);
        const data = await res.json();
        if (data.status === 'submitted' && data.location) {
          setShareStatus('submitted');
          const loc = {
            lat: data.location.lat, lng: data.location.lng,
            name: data.location.address || 'Verified destination',
            address: data.location.address || '', parish: 'Kingston', town: '',
            source: 'shared', verified: true,
          };
          setDestination(loc);
          placeDropoffMarker(loc);
          setShowShareModal(false);
          setStep(STEPS.CONFIRM_DEST);
          toast.success('Destination confirmed by recipient!');
        }
      } catch (e) { /* silent */ }
    };
    pollTimerRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollTimerRef.current);
  }, [shareStatus, shareToken, placeDropoffMarker]);

  // Book ride (P0 fix: includes payment_method + currency; P1 fix: clear booking state transitions)
  const bookRide = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // P1 fix: lock fare + payment method + full state before login redirect
      const fareToLock = lockedFare || fareEstimate?.estimate;
      sessionStorage.setItem('post_login_redirect', '/ride/book');
      sessionStorage.setItem('pendingMapBooking', JSON.stringify({
        destination, pickup, vehicleType, driverNotes, paymentMethod,
        lockedFare: fareToLock,
        lockedRouteInfo: lockedRouteInfo || routeInfo,
        paymentIntentId,
        step: STEPS.PAYMENT, shareStatus, shareToken,
      }));
      window.location.href = '/login';
      return;
    }

    // P0 GUARD: Require payment authorization before booking
    if (!paymentIntentId && !fygaroRef) {
      toast.error('Card payment authorization is required before booking.');
      setStep(STEPS.PAYMENT);
      return;
    }

    setBookingState('submitting');
    setSubmitting(true);

    // Determine currency from payment method for backend alignment
    const currency = paymentMethod === 'stripe' ? 'USD' : 'JMD';

    try {
      const res = await fetch(`${API_URL}/api/rides`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          pickup: { address: pickup.address || pickup.name || '', coordinates: { lat: pickup.lat, lng: pickup.lng }, parish: pickup.parish || 'Kingston', city: pickup.town || '' },
          dropoff: { address: destination.address || destination.name || '', coordinates: { lat: destination.lat, lng: destination.lng }, parish: destination.parish || 'Kingston', city: destination.town || '' },
          vehicle_type: vehicleType,
          ride_type: 'on_demand',
          payment_method: paymentMethod,
          currency: currency,
          passenger_count: 1,
          driver_notes: driverNotes.trim() || null,
          destination_verified: shareStatus === 'submitted',
          destination_share_token: shareToken || null,
          route_distance_km: (lockedRouteInfo || routeInfo) ? parseFloat((lockedRouteInfo || routeInfo).distance) : null,
          route_duration_min: (lockedRouteInfo || routeInfo) ? (lockedRouteInfo || routeInfo).duration : null,
          payment_intent_id: paymentIntentId || null,
          fygaro_ride_ref: fygaroRef || null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBookedRide({
          id: data.ride?.id,
          pickup: pickup,
          dropoff: destination,
          fare: displayFare,
          fareJMD: fareJMD,
          fareUSD: fareUSD,
          paymentMethod: paymentMethod,
          vehicleType: vehicleType,
          driverNotes: driverNotes.trim(),
          tripPin: data.ride?.trip_pin,
          routeInfo: routeInfo,
        });
        setBookingState('confirmed');
        sessionStorage.removeItem('pendingMapBooking');
        // Learn trip completion for destination
        if (destination) learnTripCompletion(destination.name || '', destination.lat, destination.lng, true);
        // Show post-trip confirmation prompt after short delay
        setTimeout(() => setShowPostTrip(true), 2000);
      } else {
        setBookingState(null);
        toast.error(data.detail || data.error || 'Booking failed. Please try again.');
      }
    } catch (e) {
      setBookingState(null);
      toast.error('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  // Auto-create ride after successful card authorization
  useEffect(() => {
    if (paymentIntentId && step === STEPS.PAYMENT && !bookingState) {
      bookRide();
    }
  }, [paymentIntentId]);

  // 5-minute countdown after booking confirmed (driver search window)
  useEffect(() => {
    if (bookingState !== 'confirmed') { setBookingCountdown(null); return; }
    setBookingCountdown(300);
    const timer = setInterval(() => {
      setBookingCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [bookingState]);

  // Handle Fygaro return (customer comes back from Fygaro payment page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fygaro_return') !== '1') return;
    const rideRef = params.get('ride_ref');
    const fygaroRefParam = params.get('fygaro_ref');
    if (!rideRef) return;

    // Clean URL
    window.history.replaceState({}, '', '/ride/book');

    // Restore booking context
    const saved = sessionStorage.getItem('fygaroBookingContext');
    if (saved) {
      try {
        const ctx = JSON.parse(saved);
        if (ctx.destination) { setDestination(ctx.destination); setTimeout(() => placeDropoffMarker(ctx.destination), 600); }
        if (ctx.pickup) { setPickup(ctx.pickup); setTimeout(() => placePickupMarker(ctx.pickup), 600); }
        if (ctx.vehicleType) setVehicleType(ctx.vehicleType);
        if (ctx.driverNotes) setDriverNotes(ctx.driverNotes);
        if (ctx.lockedFare) setLockedFare(ctx.lockedFare);
        if (ctx.lockedRouteInfo) setLockedRouteInfo(ctx.lockedRouteInfo);
        setPaymentMethod('fygaro');
        setFygaroRef(rideRef);
        setStep(STEPS.PAYMENT);
        sessionStorage.removeItem('fygaroBookingContext');
        toast.success('Welcome back! Verifying your Fygaro payment...');

        // Verify Fygaro payment and create ride
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        if (token) {
          fetch(`${API_URL}/api/fygaro/verify/${rideRef}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
          .then(r => r.json())
          .then(data => {
            if (data.authorized) {
              // Set fygaro ref so bookRide can use it
              setFygaroRef(rideRef);
            } else {
              toast.error('Fygaro payment not yet confirmed. Please wait or try again.');
            }
          })
          .catch(() => toast.error('Could not verify payment. Please try again.'));
        }
      } catch (e) { console.error('Failed to restore Fygaro context', e); }
    }
  }, [placeDropoffMarker, placePickupMarker]);

  // Auto-create ride after Fygaro verification
  useEffect(() => {
    if (fygaroRef && paymentMethod === 'fygaro' && step === STEPS.PAYMENT && !bookingState && !paymentIntentId) {
      // Small delay to let state settle
      const timer = setTimeout(() => bookRide(), 500);
      return () => clearTimeout(timer);
    }
  }, [fygaroRef, step, bookingState]);

  // Restore pending booking after login (P1 fix: restore locked fare + payment method)
  useEffect(() => {
    const saved = sessionStorage.getItem('pendingMapBooking');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.destination) { setDestination(data.destination); setTimeout(() => placeDropoffMarker(data.destination), 600); }
        if (data.pickup) { setPickup(data.pickup); setTimeout(() => placePickupMarker(data.pickup), 600); }
        if (data.vehicleType) setVehicleType(data.vehicleType);
        if (data.driverNotes) setDriverNotes(data.driverNotes);
        if (data.paymentMethod) setPaymentMethod(data.paymentMethod);
        if (data.paymentIntentId) setPaymentIntentId(data.paymentIntentId);
        if (data.lockedFare) setLockedFare(data.lockedFare);
        if (data.lockedRouteInfo) setLockedRouteInfo(data.lockedRouteInfo);
        if (data.step) setStep(data.step);
        if (data.shareStatus) setShareStatus(data.shareStatus);
        if (data.shareToken) setShareToken(data.shareToken);
        sessionStorage.removeItem('pendingMapBooking');
        toast.success('Your booking has been restored!');
      } catch (e) { /* silent */ }
    }
  }, [placeDropoffMarker, placePickupMarker]);

  const fmtJMD = (amt) => `J$${(Number(amt) || 0).toLocaleString()}`;
  const fmtUSD = (amt) => `$${(Number(amt) || 0).toFixed(2)} USD`;
  const isAuthenticated = !!localStorage.getItem('token');

  // P1 fix: Display fare uses locked fare when available
  const displayFare = lockedFare || fareEstimate?.estimate;
  const fareJMD = displayFare?.estimated_fare || displayFare?.fare_jmd || 0;
  const fareUSD = displayFare?.fare_usd || Math.round((fareJMD / JMD_TO_USD) * 100) / 100;
  const fareChanged = lockedFare && fareEstimate?.estimate &&
    (lockedFare.estimated_fare || lockedFare.fare_jmd) !== (fareEstimate.estimate.estimated_fare || fareEstimate.estimate.fare_jmd);

  return (
    <div className="h-screen w-screen relative overflow-hidden bg-gray-100" data-testid="map-book-ride">
      {/* Full-screen map */}
      <div ref={mapRef} className="absolute inset-0 z-0" data-testid="booking-map" />

      {/* Top buttons */}
      <div className="absolute top-4 left-4 z-[1000]">
        <button onClick={() => navigate(-1)} className="bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg hover:bg-white transition" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
      </div>
      <div className="absolute top-4 right-16 z-[1000]">
        <button onClick={recenterMap} className="bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg hover:bg-white transition" data-testid="recenter-btn">
          <Crosshair className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Destination verification badge */}
      {shareStatus === 'submitted' && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-green-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg" data-testid="verified-badge">
          <CheckCircle className="w-3.5 h-3.5" /> Destination verified by recipient
        </div>
      )}
      {shareStatus === 'pending' && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-amber-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg animate-pulse" data-testid="pending-badge">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for destination confirmation...
        </div>
      )}

      {/* ─── BOOKING STATE OVERLAY ─── */}
      {bookingState && (
        <div className="absolute inset-0 z-[2000] bg-black/60 flex items-center justify-center" data-testid="booking-state-overlay">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl overflow-y-auto" style={{maxHeight:'90vh'}}>
            {bookingState === 'submitting' && (
              <div className="text-center py-4">
                <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-1" data-testid="booking-state-title">Submitting your booking...</h3>
                <p className="text-sm text-gray-500">Please wait while we process your ride request.</p>
              </div>
            )}
            {bookingState === 'confirmed' && bookedRide && (
              <div data-testid="booking-confirmed-panel">
                <div className="text-center mb-4">
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-gray-900" data-testid="booking-state-title">Ride booked</h3>
                  <p className="text-xs text-gray-400 mt-1">Ref: {bookedRide.id}</p>
                </div>

                {/* Route summary */}
                <div className="bg-gray-50 rounded-xl p-3 mb-3 space-y-2">
                  <div className="flex items-start gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                    <div><p className="text-xs text-gray-400">Pickup</p><p className="text-sm font-medium text-gray-900">{bookedRide.pickup?.name}</p></div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                    <div><p className="text-xs text-gray-400">Destination</p><p className="text-sm font-medium text-gray-900">{bookedRide.dropoff?.name}</p></div>
                  </div>
                  {bookedRide.routeInfo && (
                    <div className="flex items-center gap-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Route className="w-3 h-3" /> {bookedRide.routeInfo.distance} km</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ~{bookedRide.routeInfo.duration} min</span>
                      <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {bookedRide.vehicleType}</span>
                    </div>
                  )}
                </div>

                {/* Fare */}
                <div className="bg-emerald-50 rounded-xl p-3 mb-3 border border-emerald-200 text-center">
                  <p className="text-xs text-gray-500 mb-0.5">Fare</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {bookedRide.paymentMethod === 'stripe' ? fmtUSD(bookedRide.fareUSD) : fmtJMD(bookedRide.fareJMD)}
                  </p>
                </div>

                {/* Driver notes */}
                {bookedRide.driverNotes && (
                  <div className="bg-blue-50 rounded-xl p-2.5 mb-3 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Driver notes</p>
                    <p className="text-xs text-gray-700 mt-0.5">{bookedRide.driverNotes}</p>
                  </div>
                )}

                {/* Trip PIN */}
                {bookedRide.tripPin && (
                  <div className="bg-amber-50 rounded-xl p-3 mb-3 border border-amber-200 text-center">
                    <p className="text-xs text-amber-700 font-medium mb-1 flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> Trip PIN</p>
                    <p className="text-2xl font-mono font-bold text-amber-800 tracking-widest">{bookedRide.tripPin}</p>
                    <p className="text-xs text-gray-500 mt-1">Share this PIN with your driver when they arrive</p>
                  </div>
                )}

                {/* Payment & next step messaging — HONEST, reflects actual state */}
                <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-200">
                  <div className="text-center" data-testid="payment-message">
                    <p className="text-sm text-gray-700 mb-1">Looking for a driver near you...</p>
                    {bookingCountdown !== null && bookingCountdown > 0 && (
                      <div className="my-2 flex items-center justify-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                        <span className="text-sm font-mono font-bold text-amber-700">{Math.floor(bookingCountdown / 60)}:{String(bookingCountdown % 60).padStart(2, '0')}</span>
                        <span className="text-xs text-gray-500">driver search</span>
                      </div>
                    )}
                    {bookingCountdown === 0 && (
                      <div className="my-2 bg-red-50 rounded-lg p-2.5 border border-red-200">
                        <p className="text-xs text-red-700 font-medium text-center">No driver found within 5 minutes.</p>
                        <p className="text-[11px] text-red-600 text-center mt-1">
                          {bookedRide?.paymentMethod === 'fygaro'
                            ? 'Your Fygaro card hold will be released shortly. You were not charged.'
                            : 'Your card hold has been automatically released. You were not charged.'}
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-emerald-700 font-medium">Your card is authorized but not charged. You will only be charged when the ride is completed.</p>
                    <p className="text-xs text-gray-500 mt-1.5">If no driver is found within 5 minutes, the hold on your card will be released automatically.</p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2">
                  {bookedRide.id && (
                    <button
                      onClick={() => navigate('/ride/track/' + bookedRide.id)}
                      className="w-full py-3.5 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 transition flex items-center justify-center gap-2"
                      data-testid="track-ride-btn">
                      <Navigation className="w-4 h-4" /> Track my ride
                    </button>
                  )}
                  <button
                    onClick={() => { setBookingState(null); setBookedRide(null); setStep(STEPS.DESTINATION); setDestination(null); setPickup(null); setDriverNotes(''); setLockedFare(null); setLockedRouteInfo(null); setPaymentIntentId(null); setFygaroRef(null); setFygaroRedirecting(false); }}
                    className="w-full py-3 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
                    data-testid="book-another-btn">
                    Book another ride
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom panel */}
      <div className="absolute bottom-0 left-0 right-0 z-[1000] bg-white rounded-t-3xl shadow-2xl overflow-auto" style={{maxHeight:"min(70vh, 70svh, 600px)"}} data-testid="booking-panel">
        <div className="flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
        <div className="px-4 pb-6 overflow-y-auto" style={{ maxHeight: 'min(calc(70vh - 20px), calc(70svh - 20px), 580px)' }}>

          {/* STEP 1: Where are you going? */}
          {step === STEPS.DESTINATION && (
            <div data-testid="step-destination">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Where are you going?</h2>

              {/* Search bar (P0 fix: forced dark text + visible placeholder) */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)} onInput={(e) => { if (e.target.value !== searchQuery) handleSearch(e.target.value); }} onFocus={() => searchResults.length > 0 && setShowResults(true)}
                  placeholder="Search address, landmark, business, plaza..."
                  className={`w-full pl-10 pr-10 py-3.5 ${INPUT_STYLE} text-base focus:ring-amber-400 focus:border-transparent`}
                  style={INPUT_COLOR}
                  autoComplete="off" autoCorrect="off" spellCheck="false" inputMode="search" data-testid="destination-search-input" />
                {searchQuery && <button onClick={() => { setSearchQuery(''); setSearchResults([]); setShowResults(false); setSearchError(null); }} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-gray-400" /></button>}
              </div>

              {/* Search results (P1 fix: loading, error, no-results states) */}
              {showResults && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto mb-3" data-testid="search-results">
                  {searching && (
                    <div className="flex items-center gap-2 px-4 py-3 text-gray-500 text-sm" data-testid="search-loading">
                      <Loader2 className="w-4 h-4 animate-spin" /> Searching Jamaica...
                    </div>
                  )}
                  {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                    <div className="px-4 py-3 text-sm" data-testid="search-no-results">
                      <p className={searchError ? "text-red-500 flex items-center gap-1.5" : "text-gray-500"}>
                        {searchError && <AlertCircle className="w-3.5 h-3.5 inline flex-shrink-0" />}
                        {searchError || 'No exact match yet. Try typing a street, community, or landmark nearby.'}
                      </p>
                    </div>
                  )}
                  {searchResults.map((r, i) => (
                    <button key={r.id || i} onClick={() => selectDestination(r)} className="w-full text-left px-4 py-3 hover:bg-amber-50 border-b border-gray-100 last:border-0 transition" data-testid={`search-result-${i}`}>
                      <div className="flex items-start gap-3">
                        <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${r.source === 'learned' ? 'text-emerald-500' : 'text-red-500'}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 text-sm">
                            {r.name}
                            {r.exact_match && (
                              <span className="ml-1.5 text-xs font-normal text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Exact match</span>
                            )}
                            {!r.exact_match && r.is_approximate && (
                              <span className="ml-1.5 text-xs font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">Approximate</span>
                            )}
                            {r.source === 'learned' && r.confidence && r.confidence !== 'suggested' && !r.exact_match && (
                              <span className="ml-1.5 text-xs font-normal text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">{r.confidence === 'trusted' ? 'Trusted' : 'Verified'}</span>
                            )}
                            {r.source === 'recent' && (
                              <span className="ml-1.5 text-xs font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">Used before</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {r.parish && <span className="font-medium text-gray-600">{r.parish}</span>}
                            {r.parish && (r.town || r.community) && <span className="text-gray-400"> · </span>}
                            {(r.town || r.community) && <span>{r.town || r.community}</span>}
                            {!r.parish && !r.town && r.display && <span>{r.display}</span>}
                          </p>
                        </div>
                        {r.distance_km != null && (
                          <div className="ml-auto flex-shrink-0 text-right mt-0.5">
                            <span className={`text-xs font-medium whitespace-nowrap ${r.distance_km > 50 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>~{r.distance_km} km</span>
                            {r.distance_km > 100 && <p className="text-xs text-red-500 leading-tight font-medium">Very far</p>}
                            {r.distance_km > 50 && r.distance_km <= 100 && <p className="text-xs text-amber-500 leading-tight">Far</p>}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}



              {/* Recent locations */}
              {!showResults && recentLocations.length > 0 && (
                <div className="mb-3" data-testid="recent-locations">
                  <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Recent destinations</p>
                  <div className="space-y-1">
                    {recentLocations.slice(0, 3).map((rl, i) => (
                      <button key={`recent-${i}`} onClick={() => selectDestination({ ...rl, id: `recent_${i}`, source: 'recent' })}
                        className="w-full flex items-center gap-2.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-left hover:bg-amber-50 hover:border-amber-300 transition"
                        data-testid={`recent-location-${i}`}>
                        <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{rl.name}</p>
                          {rl.parish && <p className="text-xs text-gray-500">{rl.parish}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Rider-personalized shortcuts */}
              {isAuthenticated && riderLoaded && (riderPlaces.saved.length > 0 || riderPlaces.recent.length > 0 || riderPlaces.frequent.length > 0) && !showResults && (
                <div className="mb-3 space-y-3">
                  {riderPlaces.saved.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1.5">Your places</p>
                      <div className="flex flex-wrap gap-2">
                        {riderPlaces.saved.map((p, i) => (
                          <button key={i} onClick={() => selectDestination({ name: p.name, lat: p.lat, lng: p.lng, parish: p.parish, source: 'saved' })}
                            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-full text-sm text-emerald-700 hover:bg-emerald-100 transition" data-testid={`saved-place-${i}`}>
                            {p.label === 'Home' && <Home className="w-3.5 h-3.5" />}
                            {p.label === 'Work' && <Briefcase className="w-3.5 h-3.5" />}
                            {!['Home','Work'].includes(p.label) && <MapPin className="w-3.5 h-3.5" />}
                            <span className="font-medium">{p.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {riderPlaces.frequent.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1.5">Frequently booked</p>
                      <div className="flex flex-wrap gap-2">
                        {riderPlaces.frequent.slice(0, 4).map((p, i) => (
                          <button key={i} onClick={() => selectDestination({ name: p.name, lat: p.lat, lng: p.lng, parish: p.parish, source: 'frequent' })}
                            className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-full text-sm text-amber-700 hover:bg-amber-100 transition" data-testid={`frequent-dest-${i}`}>
                            <Sparkles className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[140px]">{p.name}</span>
                            <span className="text-xs text-amber-500">{p.count}x</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {riderPlaces.recent.length > 0 && riderPlaces.frequent.length === 0 && (
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1.5">Recent destinations</p>
                      <div className="flex flex-wrap gap-2">
                        {riderPlaces.recent.slice(0, 4).map((p, i) => (
                          <button key={i} onClick={() => selectDestination({ name: p.name, lat: p.lat, lng: p.lng, parish: p.parish, source: 'recent' })}
                            className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700 hover:bg-blue-100 transition" data-testid={`recent-dest-${i}`}>
                            <Clock className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[140px]">{p.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Quick picks */}
              {!showResults && (
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Popular destinations</p>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="quick-picks">
                    {QUICK_PICKS.map((qp) => (
                      <button key={qp.name} onClick={() => selectDestination({ ...qp, id: `qp_${qp.name}`, source: 'curated', confidence: 0.95 })}
                        className="flex-shrink-0 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-amber-50 hover:border-amber-300 transition whitespace-nowrap"
                        data-testid={`quick-pick-${qp.name.replace(/\s/g, '-').toLowerCase()}`}>
                        {qp.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick actions — prominent CTAs */}
              <div className="flex gap-2.5 mt-1">
                <button onClick={() => getCurrentLocation('destination')} disabled={gettingLocation2}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-emerald-50 border-2 border-emerald-200 rounded-xl text-sm font-semibold text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all disabled:opacity-50" data-testid="use-current-location-dest">
                  {gettingLocation2 ? <Loader2 className="w-5 h-5 animate-spin" /> : <Crosshair className="w-5 h-5" />}
                  <span>Use my location</span>
                </button>
                <button onClick={() => setShowShareModal(true)}
                  className="flex-1 flex flex-col items-center justify-center gap-1 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-all" data-testid="send-dest-request">
                  <span className="flex items-center gap-1.5 text-sm font-semibold"><Send className="w-4 h-4" /> Send location request</span>
                  <span className="text-[10px] font-normal text-amber-600/70">They'll open a map and confirm the exact spot</span>
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Confirm destination — LOCATION VERIFICATION CARD */}
          {step === STEPS.CONFIRM_DEST && destination && (
            <div data-testid="step-confirm-dest">
              <div className="flex items-center gap-2 mb-3"><div className="w-3 h-3 rounded-full bg-red-500" /><h2 className="text-lg font-bold text-gray-900">Confirm location</h2></div>

              {/* Verification card */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-3 shadow-sm" data-testid="location-verification-card">
                {/* Typed address */}
                <div className="px-4 pt-3 pb-2 border-b border-gray-100">
                  <p className="text-xs text-gray-400 mb-0.5">You entered</p>
                  <p className="font-semibold text-gray-900 text-sm" data-testid="typed-address">{typedQuery || searchQuery || destination.name}</p>
                </div>
                {/* Verified address */}
                <div className="px-4 pt-2 pb-2 border-b border-gray-100">
                  <p className="text-xs text-gray-400 mb-0.5">{destination.is_approximate ? 'Approximate location' : 'Verified location'}</p>
                  <p className="text-sm text-gray-800" data-testid="verified-address">{destination.display || `${destination.name}, ${destination.parish || 'Jamaica'}`}</p>
                </div>
                {/* Distance + coordinates */}
                <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Distance</p>
                    <p className="text-sm font-bold text-emerald-600" data-testid="dest-distance">
                      {userLocationRef.current
                        ? (() => {
                            const ref = pickup || userLocationRef.current;
                            const d = haversineKm(ref.lat, ref.lng, destination.lat, destination.lng).toFixed(1);
                            const label = pickup ? 'from pickup' : 'from you';
                            return `~${d} km ${label}`;
                          })()
                        : pickup
                          ? `~${haversineKm(pickup.lat, pickup.lng, destination.lat, destination.lng).toFixed(1)} km from pickup`
                          : 'Enable GPS for distance'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Coordinates</p>
                    <p className="text-xs font-mono text-gray-500">{destination.lat.toFixed(5)}, {destination.lng.toFixed(5)}</p>
                  </div>
                </div>
              </div>

              {/* Nearby landmark context */}
              {nearestLandmark && (
                <div className="flex items-center gap-1.5 mb-2 px-1" data-testid="nearby-landmark">
                  <MapPin className="w-3 h-3 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-gray-600">
                    Near <span className="font-semibold text-gray-800">{nearestLandmark.name}</span>
                    <span className="text-gray-400 ml-1">({nearestLandmark.distance_m < 1000 ? `${nearestLandmark.distance_m}m` : `${(nearestLandmark.distance_m / 1000).toFixed(1)}km`})</span>
                  </p>
                </div>
              )}

              {shareStatus === 'submitted' && <div className="flex items-center gap-1 text-xs text-green-600 mb-2"><CheckCircle className="w-3 h-3" /> Verified by recipient</div>}

              <p className="text-xs text-gray-400 flex items-center gap-1 mb-3"><GripVertical className="w-3 h-3" /> Drag the red pin to adjust if location is wrong</p>

              {/* Instant fare preview if we have estimate */}
              {fareEstimate?.estimate && pickup && (
                <div className="bg-emerald-50 rounded-xl p-3 mb-3 border border-emerald-200 flex items-center justify-between">
                  <span className="text-sm text-gray-600">Estimated fare</span>
                  <span className="text-lg font-bold text-emerald-600">{fmtJMD(fareEstimate.estimate.estimated_fare || fareEstimate.estimate.fare_jmd)}</span>
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => { setStep(STEPS.DESTINATION); setDestination(null); }} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50" data-testid="search-again-btn">Search again</button>
                <button onClick={() => setStep(STEPS.PICKUP)} className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition" data-testid="confirm-dest-btn">Confirm destination</button>
              </div>
            </div>
          )}

          {/* STEP 3: Pickup */}
          {step === STEPS.PICKUP && (
            <div data-testid="step-pickup">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Where should we pick you up?</h2>
              <p className="text-sm text-gray-500 mb-3">Use GPS or search for your pickup</p>
              <button onClick={() => getCurrentLocation('pickup')} disabled={gettingLocation}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition mb-3" data-testid="gps-pickup-btn">
                {gettingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />} Use my current location
              </button>
              {/* P0 fix: dark text on pickup search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input type="text" placeholder="Or search pickup address..."
                  className={`w-full pl-9 pr-4 py-3 ${INPUT_STYLE} text-sm focus:ring-green-400`}
                  style={INPUT_COLOR}
                  autoComplete="off" autoCorrect="off" spellCheck="false" inputMode="search" data-testid="pickup-search-input"
                  onChange={(e) => {
                    const q = e.target.value;
                    if (q.length < 2) { setSearchResults([]); setShowResults(false); return; }
                    setShowResults(true); setSearching(true); setSearchError(null);
                    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                    searchTimerRef.current = setTimeout(async () => {
                      try {
                        // Same search logic as destination — landmarks alongside Nominatim
                        const landmarkResults = matchLandmarks(q);
                        const data = await nominatimSearch(q, 6);
                        const nomResults = parseNominatimResults(data);
                        const merged = [...landmarkResults, ...nomResults];
                        const seen = new Set();
                        const deduped = merged.filter((r) => {
                          const key = `${r.lat.toFixed(3)}_${r.lng.toFixed(3)}`;
                          if (seen.has(key)) return false; seen.add(key); return true;
                        });
                        const ref = destination ? { lat: destination.lat, lng: destination.lng } : userLocationRef.current;
                        for (const r of deduped) {
                          r.distance_km = ref ? parseFloat(haversineKm(ref.lat, ref.lng, r.lat, r.lng).toFixed(1)) : null;
                          r.distance_from = destination ? 'destination' : 'gps';
                        }
                        // Sort by distance within results
                        deduped.sort((a, b) => {
                          if (a.distance_km != null && b.distance_km != null) return a.distance_km - b.distance_km;
                          return 0;
                        });
                        setSearchResults(deduped.slice(0, 8));
                        if (deduped.length === 0) setSearchError('No match found.');
                      } catch (err) {
                        console.error('Pickup search error:', err);
                        setSearchResults([]);
                        setSearchError('Search failed');
                      }
                      setSearching(false);
                    }, 400);
                  }} />
              </div>
              {showResults && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto mb-3">
                  {searching && <div className="flex items-center gap-2 px-4 py-3 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Searching...</div>}
                  {!searching && searchError && <div className="flex items-center gap-2 px-4 py-3 text-red-500 text-sm"><AlertCircle className="w-4 h-4" /> {searchError}</div>}
                  {!searching && !searchError && searchResults.length === 0 && <div className="px-4 py-3 text-gray-500 text-sm">No results yet. Try a different address.</div>}
                  {searchResults.map((r, i) => (
                    <button key={r.id || i} onClick={() => {
                      const loc = { ...r, lat: Number(r.lat), lng: Number(r.lng), source: 'search' };
                      setPickup(loc); setShowResults(false); placePickupMarker(loc);
                      const d = destination ? haversineKm(loc.lat, loc.lng, destination.lat, destination.lng).toFixed(1) : null;
                      toast.success(d ? `Pickup: ${loc.name} (~${d} km to destination)` : `Pickup: ${loc.name}`);
                    }} className="w-full text-left px-4 py-2.5 hover:bg-green-50 border-b border-gray-100 last:border-0 text-sm" data-testid={`pickup-result-${i}`}>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 flex-1">{r.name}</p>
                        {r.distance_km != null && (
                          <span className={`text-xs font-medium flex-shrink-0 ${r.distance_km > 100 ? 'text-red-500 font-semibold' : r.distance_km > 50 ? 'text-amber-500' : 'text-gray-500'}`}>
                            ~{r.distance_km} km{r.distance_km > 100 ? ' Very far' : r.distance_km > 50 ? ' Far' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {r.parish && <span className="font-medium text-gray-600">{r.parish}</span>}
                        {r.parish && r.town && <span className="text-gray-400"> · </span>}
                        {r.town && <span>{r.town}</span>}
                        {!r.parish && (r.display || '')}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {pickup && (
                <div className="bg-green-50 rounded-xl p-3 mb-3 border border-green-200">
                  <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-green-500" /><p className="font-medium text-gray-900 text-sm">{pickup.name}</p></div>
                  <p className="text-xs text-gray-500 ml-5">{pickup.display || pickup.parish}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setStep(STEPS.CONFIRM_DEST)} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600" data-testid="back-to-dest-btn">Back</button>
                <button onClick={() => { if (!pickup) { toast.error('Set pickup first'); return; } setStep(STEPS.ROUTE_REVIEW); }} disabled={!pickup}
                  className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition disabled:opacity-50" data-testid="confirm-pickup-btn">Continue</button>
              </div>
            </div>
          )}

          {/* STEP 4: Route review */}
          {step === STEPS.ROUTE_REVIEW && pickup && destination && (
            <div data-testid="step-route-review">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Your ride</h2>
              <div className="bg-gray-50 rounded-xl p-4 mb-3 space-y-3">
                <div className="flex items-start gap-3"><div className="w-3 h-3 rounded-full bg-green-500 mt-1 flex-shrink-0" /><div><p className="text-sm font-medium text-gray-900">{pickup.name}</p><p className="text-xs text-gray-500">{pickup.display || pickup.parish}</p></div></div>
                <div className="ml-1.5 border-l-2 border-dashed border-gray-300 h-4" />
                <div className="flex items-start gap-3"><div className="w-3 h-3 rounded-full bg-red-500 mt-1 flex-shrink-0" /><div><p className="text-sm font-medium text-gray-900">{destination.name}</p><p className="text-xs text-gray-500">{destination.display || destination.parish}</p></div></div>
              </div>
              {routeInfo && (
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center border border-amber-200"><Route className="w-5 h-5 text-amber-600 mx-auto mb-1" /><p className="text-lg font-bold text-gray-900">{routeInfo.distance} km</p><p className="text-xs text-gray-500">Distance</p></div>
                  <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center border border-amber-200"><Clock className="w-5 h-5 text-amber-600 mx-auto mb-1" /><p className="text-lg font-bold text-gray-900">~{routeInfo.duration} min</p><p className="text-xs text-gray-500">Est. time</p></div>
                </div>
              )}
              <div className="bg-emerald-50 rounded-xl p-4 mb-3 border border-emerald-200 text-center" data-testid="fare-display">
                {fareLoading ? <div className="flex items-center justify-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Calculating...</div>
                  : fareEstimate?.estimate ? <>
                    <p className="text-xs text-gray-500 mb-1">Estimated fare</p>
                    <p className="text-3xl font-bold text-emerald-600">{fmtJMD(fareEstimate.estimate.estimated_fare || fareEstimate.estimate.fare_jmd)}</p>
                    <p className="text-xs text-gray-400 mt-1">Final fare may vary based on actual route</p>
                  </> : <p className="text-sm text-gray-500">Unable to calculate fare</p>}
              </div>
              <div className="flex gap-2 mb-4">
                {[{ key: 'standard', label: 'Standard', mult: '1x' }, { key: 'comfort', label: 'Comfort', mult: '1.3x' }, { key: 'xl', label: 'XL', mult: '1.5x' }].map(v => (
                  <button key={v.key} onClick={() => setVehicleType(v.key)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition ${vehicleType === v.key ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-testid={`vehicle-${v.key}`}>
                    <Car className="w-4 h-4 mx-auto mb-0.5" />{v.label}<span className="block text-xs opacity-60">{v.mult}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep(STEPS.PICKUP)} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600" data-testid="back-to-pickup-btn">Back</button>
                <button onClick={() => {
                  // P1 fix: Lock fare AND route when user confirms
                  if (fareEstimate?.estimate) setLockedFare(fareEstimate.estimate);
                  if (routeInfo) setLockedRouteInfo({...routeInfo});
                  setStep(STEPS.DRIVER_NOTES);
                }} className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition" data-testid="confirm-route-btn">This is correct</button>
              </div>
            </div>
          )}

          {/* STEP 5: Driver notes */}
          {step === STEPS.DRIVER_NOTES && (
            <div data-testid="step-driver-notes">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Help the driver find you</h2>
              <p className="text-sm text-gray-500 mb-3">Add address details or directions to help the driver find you faster. Optional.</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                <p className="text-xs text-amber-700 font-medium">Map location is the main destination. These extra details only help the driver find you faster.</p>
              </div>
              {/* P0 fix: dark text on textarea */}
              <textarea value={driverNotes} onChange={(e) => setDriverNotes(e.target.value)}
                placeholder="Examples: gate color, unit number, scheme name, ask for a person, near a landmark, special access..."
                className={`w-full p-4 ${INPUT_STYLE} text-sm resize-none focus:ring-amber-400`}
                style={INPUT_COLOR}
                rows={4} data-testid="driver-notes-input" />
              <div className="flex gap-2 mt-4">
                <button onClick={() => setStep(STEPS.ROUTE_REVIEW)} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600" data-testid="back-to-route-btn">Back</button>
                <button onClick={() => setStep(STEPS.SUMMARY)} className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition" data-testid="continue-to-summary-btn">
                  {driverNotes.trim() ? 'Continue' : 'Skip & continue'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: Confirm & Pay (P0 fix: explicit payment method selection) */}
          {step === STEPS.SUMMARY && pickup && destination && (
            <div data-testid="step-summary">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Confirm & pay</h2>

              {/* Route summary */}
              <div className="bg-gray-50 rounded-xl p-3 mb-3 space-y-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                  <div><p className="text-xs text-gray-400">Pickup</p><p className="text-sm font-medium text-gray-900">{pickup.name}</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Destination {shareStatus === 'submitted' && <span className="text-green-500 ml-1">Verified</span>}</p>
                    <p className="text-sm font-medium text-gray-900">{destination.name}</p>
                  </div>
                </div>
                {(lockedRouteInfo || routeInfo) && (
                  <div className="flex items-center gap-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Route className="w-3 h-3" /> {(lockedRouteInfo || routeInfo).distance} km</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ~{(lockedRouteInfo || routeInfo).duration} min</span>
                    <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {vehicleType}</span>
                  </div>
                )}
              </div>

              {driverNotes.trim() && (
                <div className="bg-blue-50 rounded-xl p-2.5 mb-3 border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Driver notes</p>
                  <p className="text-xs text-gray-700 mt-0.5">{driverNotes}</p>
                </div>
              )}

              {/* P1 fix: Fare changed notice */}
              {fareChanged && (
                <div className="bg-amber-50 rounded-xl p-2.5 mb-3 border border-amber-200" data-testid="fare-changed-notice">
                  <p className="text-xs text-amber-700 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    Fare was recalculated because your route details were updated. Your locked fare is shown below.
                  </p>
                </div>
              )}

              {/* P0 fix: Explicit payment method selection */}
              <div className="mb-3" data-testid="payment-selection">
                <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4 text-emerald-600" /> How would you like to pay?
                </p>
                <div className="space-y-2">
                  {/* Fygaro (JMD) */}
                  <button onClick={() => setPaymentMethod('fygaro')}
                    className={`w-full p-3 rounded-xl border-2 text-left transition flex items-center gap-3 ${paymentMethod === 'fygaro' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    data-testid="payment-fygaro">
                    <Wallet className={`w-5 h-5 flex-shrink-0 ${paymentMethod === 'fygaro' ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Pay in JMD</p>
                      <p className="text-xs text-gray-500">Fygaro — Jamaican card payment</p>
                    </div>
                    {displayFare && <p className={`text-sm font-bold flex-shrink-0 ${paymentMethod === 'fygaro' ? 'text-emerald-600' : 'text-gray-700'}`}>{fmtJMD(fareJMD)}</p>}
                  </button>

                  {/* Stripe (USD) */}
                  <button onClick={() => setPaymentMethod('stripe')}
                    className={`w-full p-3 rounded-xl border-2 text-left transition flex items-center gap-3 ${paymentMethod === 'stripe' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    data-testid="payment-stripe">
                    <CreditCard className={`w-5 h-5 flex-shrink-0 ${paymentMethod === 'stripe' ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Pay in USD</p>
                      <p className="text-xs text-gray-500">Stripe — international card payment</p>
                    </div>
                    {displayFare && <p className={`text-sm font-bold flex-shrink-0 ${paymentMethod === 'stripe' ? 'text-emerald-600' : 'text-gray-700'}`}>{fmtUSD(fareUSD)}</p>}
                  </button>

                </div>

                {/* Payment explanation */}
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  {paymentMethod === 'fygaro' ? (
                    <div className="text-center space-y-1">
                      <p className="text-xs text-gray-700 font-medium">You will be securely redirected to Fygaro</p>
                      <p className="text-[11px] text-gray-500">Enter your card details on Fygaro's secure page. Your card will be authorized (not charged) until your ride is completed.</p>
                      <p className="text-[11px] text-gray-500">After payment, you will return here automatically. No cash.</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 text-center">Card payment required before ride is booked. No cash.</p>
                  )}
                </div>
              </div>

              {/* Fare display */}
              {displayFare && (
                <div className="bg-emerald-50 rounded-xl p-3 mb-3 border border-emerald-200 text-center" data-testid="fare-locked">
                  <p className="text-xs text-gray-500 mb-0.5">Your fare</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {paymentMethod === 'stripe' ? fmtUSD(fareUSD) : fmtJMD(fareJMD)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {lockedFare ? 'Fare locked at time of route confirmation' : 'Estimated fare'}
                  </p>
                </div>
              )}

              {!isAuthenticated && (
                <div className="bg-amber-50 rounded-xl p-3 mb-3 border border-amber-200 text-center">
                  <p className="text-sm text-amber-700">Sign in to complete your booking</p>
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => setStep(STEPS.DRIVER_NOTES)} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600" data-testid="back-to-notes-btn">Back</button>
                <button onClick={async () => {
                  if (!isAuthenticated) { bookRide(); return; }
                  if (paymentMethod === 'fygaro') {
                    // Fygaro JMD: redirect to Fygaro payment page
                    setFygaroRedirecting(true);
                    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
                    const rideRef = `ride_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
                    try {
                      const res = await fetch(`${API_URL}/api/fygaro/create-link`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ amount: fareJMD, ride_ref: rideRef }),
                      });
                      const data = await res.json();
                      if (data.success && data.payment_url) {
                        // Save full booking context before redirect
                        sessionStorage.setItem('fygaroBookingContext', JSON.stringify({
                          destination, pickup, vehicleType, driverNotes, paymentMethod: 'fygaro',
                          lockedFare, lockedRouteInfo, shareStatus, shareToken, rideRef,
                        }));
                        window.location.href = data.payment_url;
                      } else {
                        toast.error(data.detail || 'Could not create Fygaro payment link');
                        setFygaroRedirecting(false);
                      }
                    } catch (e) {
                      toast.error('Network error. Please try again.');
                      setFygaroRedirecting(false);
                    }
                  } else {
                    setStep(STEPS.PAYMENT);
                  }
                }}
                  disabled={fygaroRedirecting}
                  className="flex-1 py-3.5 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 transition disabled:opacity-50" data-testid="proceed-payment-btn">
                  {fygaroRedirecting
                    ? 'Redirecting to Fygaro...'
                    : isAuthenticated
                      ? paymentMethod === 'fygaro'
                        ? `Pay ${fmtJMD(fareJMD)} via Fygaro`
                        : `Proceed to payment ${fmtUSD(fareUSD)}`
                      : 'Sign in & book'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 7: Card Payment (Stripe Elements or Fygaro return) */}
          {step === STEPS.PAYMENT && pickup && destination && isAuthenticated && (
            <div data-testid="step-payment">
              {paymentMethod === 'fygaro' && fygaroRef ? (
                <>
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Fygaro payment verified</h2>
                  <div className="bg-emerald-50 rounded-xl p-4 mb-3 border border-emerald-200 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-emerald-700">JMD card authorized via Fygaro</p>
                    <p className="text-xs text-emerald-600 mt-1">Your card is held but not charged. You will only be charged when your ride is completed.</p>
                    <p className="text-xs text-gray-500 mt-1.5">If no driver is found within 5 minutes, the hold will be released.</p>
                    <Loader2 className="w-5 h-5 text-emerald-500 animate-spin mx-auto mt-2" />
                  </div>
                </>
              ) : paymentMethod === 'fygaro' ? (
                <>
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Redirecting to Fygaro</h2>
                  <div className="text-center py-4">
                    <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-600">Taking you to Fygaro secure payment...</p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Pay by card</h2>
                  <p className="text-sm text-gray-500 mb-3">Enter your card details to authorize payment for this ride.</p>

              {/* Compact route recap */}
              <div className="bg-gray-50 rounded-xl p-2.5 mb-3 flex items-center gap-2 text-xs text-gray-600">
                <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                <span className="truncate">{pickup.name}</span>
                <ArrowRight className="w-3 h-3 flex-shrink-0 text-gray-400" />
                <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <span className="truncate">{destination.name}</span>
              </div>

              <StripePaymentForm
                amount={paymentMethod === 'stripe' ? fareUSD : fareJMD}
                currency={paymentMethod === 'stripe' ? 'USD' : 'JMD'}
                onPaymentComplete={(piId) => {
                  setPaymentIntentId(piId);
                }}
                onCancel={() => setStep(STEPS.SUMMARY)}
                submitting={submitting}
                setSubmitting={setSubmitting}
              />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Destination Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[2000] bg-black/50 flex items-end justify-center" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-8" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Send destination request</h3>
              <button onClick={() => setShowShareModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Send a link to someone at the destination. They can confirm or adjust the exact location on a map.</p>

            {!shareToken ? (
              <>
                {/* P0 fix: dark text on share modal inputs */}
                <input type="text" value={shareRecipientName} onChange={(e) => setShareRecipientName(e.target.value)}
                  placeholder="Recipient name"
                  className={`w-full px-4 py-3 ${INPUT_STYLE} text-sm mb-3 focus:ring-amber-400`}
                  style={INPUT_COLOR}
                  data-testid="share-recipient-name" />
                <input type="tel" value={shareRecipientPhone} onChange={(e) => setShareRecipientPhone(e.target.value)}
                  placeholder="Phone number (for WhatsApp)"
                  className={`w-full px-4 py-3 ${INPUT_STYLE} text-sm mb-4 focus:ring-amber-400`}
                  style={INPUT_COLOR}
                  data-testid="share-recipient-phone" />
                <button onClick={sendDestinationRequest} disabled={shareSending}
                  className="w-full py-3.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition disabled:opacity-50" data-testid="send-share-btn">
                  {shareSending ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Sending...</> : <><Send className="w-4 h-4 inline mr-1" /> Generate & send link</>}
                </button>
              </>
            ) : (
              <div>
                <div className="bg-green-50 rounded-xl p-4 mb-4 border border-green-200">
                  <p className="text-sm font-medium text-green-700 flex items-center gap-1 mb-2"><CheckCircle className="w-4 h-4" /> Link created!</p>
                  <p className="text-xs text-gray-600 break-all bg-white rounded-lg p-2 border">{shareUrl}</p>
                </div>
                <div className="flex gap-2 mb-3">
                  <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); }}
                    className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-600 flex items-center justify-center gap-1" data-testid="copy-share-link"><Copy className="w-4 h-4" /> Copy</button>
                  <button onClick={() => { const phone = shareRecipientPhone.replace(/[^0-9]/g, ''); const msg = encodeURIComponent(`Hi! Please confirm the destination for my ride by tapping this link: ${shareUrl}`); window.open(`https://wa.me/${phone}?text=${msg}`, '_blank'); }}
                    className="flex-1 py-3 bg-green-500 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-1" data-testid="whatsapp-share-btn"><ExternalLink className="w-4 h-4" /> WhatsApp</button>
                </div>
                <p className="text-center text-xs text-gray-400">{shareStatus === 'pending' ? 'Waiting for recipient to confirm...' : shareStatus === 'submitted' ? 'Destination confirmed!' : ''}</p>
                <button onClick={() => setShowShareModal(false)} className="w-full mt-3 py-3 border border-gray-200 rounded-xl text-sm text-gray-600">Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Post-Trip Confirmation Prompt */}
      {showPostTrip && bookedRide && (
        <PostTripConfirmation
          rideId={bookedRide.id}
          destination={{
            name: bookedRide.dropoff?.name || destination?.name || '',
            lat: destination?.lat || 0,
            lng: destination?.lng || 0,
            address: bookedRide.dropoff?.name || '',
          }}
          onDismiss={() => setShowPostTrip(false)}
        />
      )}
    </div>
  );
}
